import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DocumentTitle from 'src/components/DocumentTitle/DocumentTitle';
import ContentPage from 'src/components/ContentPage/ContentPage';
import useDocumentTitle from 'src/hooks/use-document-title';
import saveAs from 'file-saver';
import { useSivaContractDataExtract } from 'src/features/siva/ideas-and-companies/hooks/use-siva-contract-data-extract';
import { LoadingPage } from 'src/components/LoadingPage/LoadingPage';
import { Button, Field, Icon, Label, Select } from '@innovationnorway/cl-react';
import useSivaYearList from 'src/hooks/use-year-list-reporting';

const ExtractIdeasAndCompanies: React.FC = () => {
  const { t: tPageTitles } = useTranslation('pageTitles');
  const { t: tSiva } = useTranslation('siva');
  const { t: tCommon } = useTranslation('common');
  const { documentTitle } = useDocumentTitle(tPageTitles('sivaReporting'));

  const [selectedYear, setSelectedYear] = useState<string | undefined>(undefined);
  const yearList = useSivaYearList(2023);

  const { downloadAllData } = useSivaContractDataExtract(
    selectedYear === '' ? undefined : parseInt(selectedYear || ''),
  );

  if (downloadAllData.isLoading) {
    return (
      <DocumentTitle title={documentTitle}>
        <LoadingPage />
      </DocumentTitle>
    );
  }

  return (
    <ContentPage
      title={tSiva('export.ideasAndCompanies.header')}
      documentTitle={tPageTitles('sivaReporting')}
    >
      <Field>
        <Label>{tSiva('export.ideasAndCompanies.yearLabel')}</Label>
        <Select
          name="year"
          value={selectedYear ?? 'none'}
          className="margin-0-on-all-children"
          onChange={(event) => setSelectedYear(event.target.value)}
        >
          {selectedYear === undefined && (
            <Select.Option value="none" disabled>
              {tCommon('selectYear')}
            </Select.Option>
          )}
          <Select.Option value="">{tCommon('allYears')}</Select.Option>
          {yearList.map((year) => (
            <Select.Option key={year} value={year.toString()}>
              {year}
            </Select.Option>
          ))}
        </Select>
      </Field>
      <Button
        type="button"
        variant="tertiary"
        onClick={async () => {
          const file = await downloadAllData.mutateAsync(true);
          saveAs(file.data, file.fileName);
        }}
        disabled={selectedYear === undefined}
      >
        <Icon prefix="fasr" iconName="file-export" />
        {tSiva('export.ideasAndCompanies.exportButton')}
      </Button>
    </ContentPage>
  );
};

export default ExtractIdeasAndCompanies;
